import { AuthStatusType, useAuthState } from '../state/AuthState';
import { isRunningInNativeWrapper } from '../../Native';
import { Navigate, useParams } from 'react-router-dom';
import { SpinnerAppLoad } from 'dg-web-shared/pc-ui/md/layout/SpinnerAppLoad.tsx';
import { useServerFetch } from '../../hooks/ServerStateHooks.ts';
import { RequestStatus } from 'dg-web-shared/lib/hooks/ServerStateHooks.ts';
import { ParkingpayGenericError } from '../../common/components/ParkingpayGenericError.tsx';
import {
    TicketAndState,
    TicketReceipt,
    TicketReceiptType,
} from 'dg-web-shared/model/Ticket.ts';

interface CheckTicketResponse {
    ticketReceipt: TicketReceipt;
    ticketAndState: TicketAndState | null;
    hasPayStationPaymentReceipt: boolean;
}

export function DeepLinkPunchCardTicketConsumer() {
    const { ticketBase64 } = useParams<{ ticketBase64: string }>();

    const [requestState] = useServerFetch<
        CheckTicketResponse,
        { ticketBase64?: string },
        null
    >(
        c => ({
            url: `/ui-api/check-ticket/${c.ticketBase64}`,
        }),
        { ticketBase64 },
    );

    switch (requestState.status) {
        case RequestStatus.NEVER_EXECUTED:
        case RequestStatus.PENDING:
            return <SpinnerAppLoad />;
        case RequestStatus.ERROR:
            return <ParkingpayGenericError />;
        case RequestStatus.SUCCESS: {
            return (
                <DeepLinkTicketConsumer paystationTicket={requestState.data} />
            );
        }
    }
}

export function DeepLinkQrTicketConsumer() {
    const { ticketId } = useParams<{ ticketId: string }>();

    const [requestState] = useServerFetch<
        CheckTicketResponse,
        { ticketId?: string },
        null
    >(
        c => ({
            url: `/ui-api/check-ticket-qr/${c.ticketId}`,
        }),
        { ticketId },
    );

    switch (requestState.status) {
        case RequestStatus.NEVER_EXECUTED:
        case RequestStatus.PENDING:
            return <SpinnerAppLoad />;
        case RequestStatus.ERROR:
            return <ParkingpayGenericError />;
        case RequestStatus.SUCCESS: {
            return (
                <DeepLinkTicketConsumer paystationTicket={requestState.data} />
            );
        }
    }
}

export function DeepLinkTicketConsumer({
    paystationTicket,
}: {
    paystationTicket: CheckTicketResponse;
}) {
    const { authStatus } = useAuthState();

    const { ticketReceipt, ticketAndState, hasPayStationPaymentReceipt } =
        paystationTicket;

    if (ticketAndState === null) {
        // ticket not found, redirect to receipt page
        switch (ticketReceipt.receiptType) {
            case TicketReceiptType.PUNCH_CARD:
                return (
                    <Navigate
                        to={`/quickcheckout/ticket-receipt/${ticketReceipt.ticketBase64}`}
                        replace
                    />
                );
            case TicketReceiptType.QR_TICKET:
                return (
                    <Navigate
                        to={`/quickcheckout/ticket-receipt-qr/${ticketReceipt.ticketId}`}
                        replace
                    />
                );
        }
    }

    const ticketId = ticketAndState.ticket.ticketId;

    if (hasPayStationPaymentReceipt) {
        return <Navigate to={`/quickcheckout/ticket/${ticketId}`} replace />;
    }

    if (isRunningInNativeWrapper() || authStatus === AuthStatusType.OK) {
        return <Navigate to={`/ticket/${ticketId}`} replace />;
    }

    if (authStatus === AuthStatusType.NOT_OK) {
        return <Navigate to={`/quickcheckout/ticket/${ticketId}`} replace />;
    }

    return <SpinnerAppLoad />;
}

export function DeepLinkPermittypeConsumer() {
    const { permitUrlIdentification } = useParams<{
        permitUrlIdentification: string;
    }>();
    const { authStatus } = useAuthState();

    if (isRunningInNativeWrapper() || authStatus === AuthStatusType.OK) {
        return (
            <Navigate to={`/permittype/${permitUrlIdentification}`} replace />
        );
    }

    if (authStatus === AuthStatusType.NOT_OK) {
        return (
            <Navigate
                to={`/quickcheckout/permittype/${permitUrlIdentification}`}
                replace
            />
        );
    }

    return <SpinnerAppLoad />;
}
